/* eslint-disable react/no-array-index-key */
import * as React from 'react'
import { Drawer } from '@roo/roo'
import Loading from '@roo/roo/Loading'
import { useRequest } from 'ahooks'
import { fetchOrderDetail } from '../../utils/api'
import { useTranslation } from '@sailor/i18n-web'
import { getI18nTime, replaceMoneySymbol } from '@/common/utils/i18nFuncs'
import useOrderMaps from '../../utils/useOrderMaps'
import { OrderDetailApiData, BizOrderStatusEnum, ApiOrderBaseInfo } from '../../types'
import styles from './index.module.less'
import OrderStatusTag from '../OrderStatusTag'
import { getDateTimePattern } from '@/common/utils/i18n'
import entInfoAtom from '@/common/recoil/atom/entInfoAtom'

const RuleDetail = React.forwardRef((props: { bizOrderId: number; defaultOpen?: boolean; hideText?: boolean }, ref) => {
  const [visible, setVisible] = React.useState(false)
  const { t } = useTranslation()
  const { payTypeMap, statusMap } = useOrderMaps()
  const entInfo = entInfoAtom.useData()
  const { data: orderDetailRes, loading, run } = useRequest(() => fetchOrderDetail({ bizOrderId: props.bizOrderId }), { manual: true })
  const { orderBaseInfo, staffInfo, origOrderInfo, controlInfo } = (orderDetailRes || {}) as Partial<OrderDetailApiData>

  // 有 bizOrderId 且 defaultOpen 时，自动打开
  React.useEffect(() => {
    if (props.bizOrderId && props.defaultOpen) {
      setVisible(true)
    }
  }, [props.bizOrderId, props.defaultOpen])

  // 暴露打开方法给父组件
  React.useImperativeHandle(ref, () => ({
    open: () => setVisible(true),
  }))

  // 部分退款硬编码数据 - 改为部分退款状态
  const foodAmountDetail = origOrderInfo?.foodAmountDetail

  const result = [
    {
      title: t('e_pc_basic_info2', '基本信息'),
      list: [
        {
          title: t('e_pc_order_number', '订单号'),
          content: orderBaseInfo?.bizOrderId,
        },
        {
          title: t('e_pc_order_region', '下单 Region'),
          content: orderBaseInfo?.region,
        },
        {
          title: t('e_pc_order_time', '下单时间'),
          content: orderBaseInfo?.createdTime ? getI18nTime(orderBaseInfo?.createdTime) : '--',
        },
        {
          title: t('e_pc_order_status', '订单状态'),
          content: orderBaseInfo?.orderStatus ? <OrderStatusTag status={orderBaseInfo?.orderStatus}/> : '--',
        },
        {
          title: t('e_pc_payment_method', '支付方式'),
          content: orderBaseInfo?.payType ? payTypeMap[orderBaseInfo.payType] : '--',
        },
        {
          title: t('e_pc_order_amount', '订单金额'),
          content: replaceMoneySymbol(orderBaseInfo?.orderAmount?.displayText || '--'),
        },
        {
          title: t('e_pc_corporate_payment_amount', '企业支付金额'),
          content: replaceMoneySymbol(orderBaseInfo?.corporatePayAmount?.displayText || '--'),
        },
        {
          title: t('e_pc_personal_payment_amount', '个人支付金额'),
          content: replaceMoneySymbol(orderBaseInfo?.personalPayAmount?.displayText || '--'),
        },
        {
          title: t('e_pc_enterprise_refund_amount', '企业退款金额'),
          content: replaceMoneySymbol(orderBaseInfo?.corporateRefundAmount?.displayText || '--'),
        },
        {
          title: t('e_pc_personal_refund_amount', '个人退款金额'),
          content: replaceMoneySymbol(orderBaseInfo?.personalRefundAmount?.displayText || '--'),
        },
      ],
    },
    {
      title: t('e_pc_staff_info', '员工信息'),
      list: [
        {
          title: t('e_pc_employee_name', '姓名'),
          content: staffInfo?.staffName,
        },
        {
          title: t('e_pc_employee_id', '工号'),
          content: staffInfo?.staffEmployeeNum,
        },
        {
          title: t('e_pc_employee_email', '邮箱'),
          content: staffInfo?.staffEmail,
        },
        {
          title: t('e_pc_employee_department', '员工部门'),
          content: staffInfo?.departmentNamePath,
        },
        {
          title: t('e_pc_group_bill', '员工分组'),
          content: staffInfo?.staffCategory,
        },
        ...(entInfo?.costCenterEnabled ? [{
          title: t('e_pc_cost_center_tab', '成本中心'),
          content: staffInfo?.costCenterName || '--',
        }] : []),
      ],
    },
    {
      title: t('e_pc_order_info', '订单信息'),
      list: [
        {
          title: t('e_pc_shop_name', '店铺名称'),
          content: origOrderInfo?.shopName,
        },
        {
          title: t('e_pc_shop_address', '店家地址'),
          content: origOrderInfo?.shopAddress,
        },
        {
          title: t('e_pc_item', '商品'),
          content: foodAmountDetail?.products?.length ? (
            <div className={styles.productsWrapper}>
              {
                foodAmountDetail?.products?.map((product, idx) => (
                  <span key={idx}>
                    {product.count} * {product.name}
                    {
                      idx !== foodAmountDetail?.products?.length - 1 &&
                      <span>;&nbsp;</span> ||
                      ''
                    }
                  </span>
                ))
              }
            </div>
          ) : '--',
        },
        {
          title: t('e_pc_total_product_price', '商品总价'),
          content: replaceMoneySymbol(foodAmountDetail?.productTotalAmount?.displayText || '--'),
        },
        {
          title: t('e_pc_delivery_fee', '运费'),
          content: replaceMoneySymbol(foodAmountDetail?.shippingFee?.displayText || '--'),
        },
        {
          title: t('e_pc_platform_fee', '平台费'),
          content: replaceMoneySymbol(foodAmountDetail?.platformFee?.displayText || '--'),
        },
        {
          title: t('e_pc_tip', '贴士'),
          content: replaceMoneySymbol(foodAmountDetail?.tip?.displayText || '--'),
        },
        {
          title: t('e_pc_items_lowest_complement', '最低价补差'),
          content: replaceMoneySymbol(foodAmountDetail?.diffPrice?.displayText || '--'),
        },
        {
          title: t('e_pc_delivery_address', '送餐地址'),
          content: origOrderInfo?.recipientAddress,
        },
        {
          title: t('e_pc_total_discount_amount', '总优惠金额'),
          content: replaceMoneySymbol(origOrderInfo?.discountAmount?.displayText || '--'),
        },
        {
          title: t('e_pc_pickup_method', '取货方式'),
          content: origOrderInfo?.userGetMode ? {
            delivery: t('e_pc_delivery', '外卖'),
            pickup: t('e_pc_pickup', '自提'),
          }[origOrderInfo.userGetMode] : '--',
        },
        {
          title: t('e_pc_remark', '备注'),
          content: origOrderInfo?.remark,
        },
        ...(entInfo?.expenseCodeEnabled ? [{
          title: t('e_pc_expense_code_label', '费用代码'),
          content: controlInfo?.expenseCode || '--',
        }, {
          title: t('e_pc_expense_remark', '费用备注'),
          content: controlInfo?.expenseRemark || '--',
        }] : []),
      ],
    },
  ]

  React.useEffect(() => {
    if (visible) {
      run()
    }
  }, [visible])

  const handleClickDetail = (): void => {
    setVisible(true)
  }

  const handleDrawerClose = (): void => {
    setVisible(false)
  }

  return (
    <div>
      <Drawer
        visible={visible}
        width="800px"
        title={t('e_pc_order_details', '订单详情')}
        placement="right"
        onClose={handleDrawerClose}
        bodyStyle={{ paddingTop: 0 }}
      >
        <div>
          <Loading fullscreen lock visible={loading && visible}/>
          {result.map((item, index) => (
            <div key={item.title}>
              <div className={styles.sectionTitle} style={{ paddingTop: index === 0 ? '20px' : '28px' }}>
                {item.title}
              </div>
              <div className={styles.infoList}>
                {item.list.map((listItem: any, idx: number) => (
                  <React.Fragment key={listItem.title}>
                    {!listItem.isFood && (
                      <div className={styles.infoItem}>
                        <div className={styles.infoLabel}>{listItem.title}</div>
                        <div className={styles.infoContent}>{listItem.content || '--'}</div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Drawer>
      {!props.hideText && (
        <div onClick={handleClickDetail}>
          {t('e_pc_details', '详情')}
        </div>
      )}
    </div>
  )
})

export default RuleDetail